// brain.ts — the AI client. Calls the LIVE Oriora server brain for this app:
//   https://api.orioralabs.com/api/apps/oriora_stash/recap   (What do I have for [season]?)
//   https://api.orioralabs.com/api/apps/oriora_stash/ask     (NL query over the catalog)
// with the customer's Oriora session token. The server holds the prompt + bills the
// Oriora wallet (Managed AI / BYOK). The catalog stays local; only a DERIVED-SIGNALS
// object (per-season/-location/-category roll-ups, used-last-cycle vs never-used, duplicate
// clusters, and — for a recap — the named items + locations for ONE focused season; see
// signals.rs) is sent. The bulk catalog never leaves the device.
//
// ONE billed AI call per user action. Text-in scoped JSON, text-out. No agent loop, no
// second call, no tool-use chain, no direct OpenAI/Anthropic SDK in the app.
//
// HARD CEILING (enforced server-side, mirrored here + in our UI copy): the narration
// DESCRIBES the user's OWN inventory — what they own and where it is stored, with the
// use-log as plain fact. It NEVER advises what to buy/wear/keep/discard, and NEVER gives
// styling advice. The fence below catches advice/styling/shopping questions BEFORE we
// bill a call, and returns the canned redirect.

import { invoke } from "./tauri-web";
import { getAccessToken } from "./supabase";
import type { StashSignals } from "./store";

const SERVER = "https://api.orioralabs.com";
const SLUG = "oriora_stash";

export type BrainErrorCode = "insufficient_funds" | "managed_ai_blocked" | "unauthenticated" | "unavailable" | "no_store" | "too_large";
export class BrainError extends Error {
  code: BrainErrorCode;
  constructor(code: BrainErrorCode, message: string) {
    super(message);
    this.code = code;
    this.name = "BrainError";
  }
}

// Pre-LLM refusal fence. The app DESCRIBES owned objects + locations + use-log; it does NOT
// advise. If the question asks what to wear/buy/what's in style/what to keep or toss, we
// redirect WITHOUT billing a call. This mirrors the server-side HARD RULES.
const FENCE_PATTERNS: RegExp[] = [
  /\bwhat (should|do|can|would) i (wear|buy|get|purchase|keep|toss|throw|donate|discard|declutter)\b/i,
  /\bshould i (wear|buy|get|keep|toss|throw|donate|discard|replace|return)\b/i,
  /\b(in style|on trend|trendy|fashionable|stylish|outfit|style advice|styling|look good|match)\b/i,
  /\b(what to wear|what to buy|recommend|suggestion|advice|advise)\b/i,
  /\bdo i need (to buy|more|another|a new)\b/i,
  /\bwhere (should|can) i (buy|shop|find one)\b/i,
];

export const FENCE_REDIRECT =
  "I can show what you own and where, not give styling or shopping advice. Try asking what you have for a season, where something is stored, or what you haven't used.";

/** True when a question crosses the can-only fence (styling / buying / keep-or-toss advice). */
export function crossesFence(question: string): boolean {
  return FENCE_PATTERNS.some((re) => re.test(question));
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// SSE reader for the streaming fleet chat (request body {stream:true} → text/event-stream):
// `: open` / `: keepalive` comments and [DONE] are ignored, each `data: {"chunk":…}` event
// feeds onChunk, and the `data: {"done":true, …}` event carries the exact old JSON payload —
// returned where res.json() used to be, so all existing action/reply mapping stays untouched.
async function readSseChat(bodyStream: ReadableStream<Uint8Array>, onChunk: (t: string) => void): Promise<Record<string, unknown>> {
  const reader = bodyStream.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let done: Record<string, unknown> | null = null;
  for (;;) {
    const { value, done: eof } = await reader.read();
    if (eof) break;
    buf += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf("\n")) !== -1) {
      const line = buf.slice(0, nl).replace(/\r$/, "");
      buf = buf.slice(nl + 1);
      if (!line.startsWith("data:")) continue; // comment (": open"/": keepalive") or blank line
      const payload = line.slice(5).trim();
      if (payload === "[DONE]") continue;
      let ev: Record<string, unknown>;
      try { ev = JSON.parse(payload) as Record<string, unknown>; } catch { continue; }
      if (typeof ev.chunk === "string") onChunk(ev.chunk);
      else if (ev.done) done = ev;
    }
  }
  if (!done) throw new BrainError("unavailable", "The reply stream ended early — please try again.");
  // A streamed reply can end with a billing/auth error instead of a payload ({done:true, error, _status}).
  // Returning it as data dissolved the "Top up" message into an empty action (found live 2026-08-27) —
  // throw the same BrainError the buffered path would.
  if (typeof done.error === "string" && done.error) {
    const st = typeof done._status === "number" ? done._status : 0;
    const msg = typeof done.message === "string" && done.message ? done.message : done.error;
    if (st === 401) throw new BrainError("unauthenticated", msg);
    if (st === 402) throw new BrainError("insufficient_funds", msg);
    if (st === 403) throw new BrainError("managed_ai_blocked", msg);
    throw new BrainError("unavailable", msg);
  }
  return done;
}

export async function brainCall<T>(endpoint: string, body: unknown, onChunk?: (t: string) => void): Promise<T> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  // Retry transient failures (502/503/504 + network). Hard errors (401/402/403) never retry.
  const MAX = 3;
  for (let attempt = 1; attempt <= MAX; attempt++) {
    let res: Response;
    try {
      res = await fetch(`${SERVER}/api/apps/${SLUG}/${endpoint}`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify(onChunk && body && typeof body === "object" ? { ...(body as Record<string, unknown>), stream: true } : body),
      });
    } catch {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is temporarily unavailable.");
    }
    if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
    if (res.status === 402) throw new BrainError("insufficient_funds", "Add funds to keep using AI.");
    if (res.status === 403) throw new BrainError("managed_ai_blocked", (await res.json().catch(() => null))?.message || "Connect a vendor key in Oriora settings to use AI for this app.");
    if ([502, 503, 504].includes(res.status)) {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is busy — please try again.");
    }
    if (!res.ok) throw new BrainError("unavailable", "AI request failed.");
    // Streaming fleet chat: with onChunk the body carries stream:true and the server answers
    // as SSE — read it incrementally (readSseChat above); the done event IS the old JSON
    // payload. Anything that is not an event stream (old server, an error status body, a
    // proxy that buffered it away) falls through to the buffered path — never a dead end.
    if (onChunk && res.body && (res.headers.get("content-type") ?? "").includes("text/event-stream")) {
      return (await readSseChat(res.body, onChunk)) as unknown as T;
    }
    return (await res.json()) as T;
  }
  throw new BrainError("unavailable", "AI is temporarily unavailable.");
}

export interface RecapResult {
  narration: string;
}
export interface AskResult {
  answer: string;
  /** Server sets this true when the question hit its own refusal fence. */
  refused?: boolean;
}

/**
 * "What do I have for [season]?" — the headline billed call.
 *
 * We compute the signals on-device with the season FOCUS block populated (named items +
 * locations for that one season) and send only that derived slice. The brain narrates the
 * pre-computed picture: how many items, across how many bins/locations, which named items
 * live where, what's never been used. OBSERVATIONAL ONLY — describes the owned inventory,
 * never advises. Server prompt mirrors the same HARD RULES (operator task).
 */
export async function recapSeason(signals: StashSignals): Promise<RecapResult> {
  if (signals.overall.total_items === 0) {
    throw new BrainError("unavailable", "Catalog a few items first — there's nothing to recap yet.");
  }
  if (!signals.focus || signals.focus.total_items === 0) {
    throw new BrainError("unavailable", "You haven't cataloged anything for that season yet.");
  }
  const r = await brainCall<Partial<RecapResult>>("recap", { signals });
  return { narration: String(r.narration ?? "").trim() };
}

/**
 * "Ask your stash" — a NL query over the catalog (more billable usage per session).
 *
 * Ceiling-safe: the question is checked against the pre-LLM fence FIRST (no call billed if
 * it asks for styling/shopping/keep-or-toss advice). Otherwise we send the question + the
 * derived signals (with the relevant season focus when we can infer one) and the brain
 * answers ONLY from the supplied aggregates, naming real figures. Each question = ONE call.
 */
export async function askStash(question: string, signals: StashSignals): Promise<AskResult> {
  const q = question.trim();
  if (!q) throw new BrainError("unavailable", "Type a question about your catalog first.");
  if (crossesFence(q)) {
    // Refuse locally — no call billed.
    return { answer: FENCE_REDIRECT, refused: true };
  }
  if (signals.overall.total_items === 0) {
    throw new BrainError("unavailable", "Catalog a few items first — there's nothing to answer about yet.");
  }
  const r = await brainCall<Partial<AskResult>>("ask", { question: q, signals });
  return { answer: String(r.answer ?? "").trim(), refused: Boolean(r.refused) };
}

// ── Cloud backup (the user owns their data) ─────────────────────────────────────
// The device builds a FAITHFUL backup locally (export_backup); these relay it to the user's OWN
// connected cloud store — their GitHub repo or Google Drive (connected once in the Oriora
// account). Oriora RETAINS nothing. Plain HTTP → works on iOS. Endpoints: the generic
// POST/GET /api/apps/oriora_stash/backup (shared server route).
export interface CloudBackupState { connected: boolean; provider?: string; data?: unknown }

async function authedFetch(endpoint: string, init?: RequestInit): Promise<Response> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  return fetch(`${SERVER}/api/apps/${SLUG}/${endpoint}`, {
    ...init,
    headers: { ...(init?.headers ?? {}), authorization: `Bearer ${token}` },
  });
}

/** Save the local backup envelope to the user's connected cloud store. Throws BrainError('no_store')
 *  when nothing is connected, so the UI can point them at the Connections hub. */
export async function cloudBackupSave(envelope: unknown): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("backup", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ data: envelope }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to back up to the cloud.");
  // 413 = over the standard path's size limit (photo-heavy backup). Not retryable — the caller
  // switches to the device-direct GitHub save, or tells the customer how to enable it.
  if (res.status === 413) throw new BrainError("too_large", "This backup is too large for the standard path.");
  if (!res.ok) throw new BrainError("unavailable", "Cloud backup failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Save a note or the whole conversation (text) to the customer's OWN GitHub/Drive via the generic
 *  /store relay — Oriora retains nothing. Throws BrainError('no_store') when nothing is connected. */
export async function cloudStoreSave(
  content: string,
  opts?: { name?: string; kind?: string; destination?: "github" | "gdrive" },
): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("store", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ content, name: opts?.name, kind: opts?.kind, provider: opts?.destination }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to save to the cloud.");
  if (!res.ok) throw new BrainError("unavailable", "Saving to your cloud failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Read the cloud backup for restore. `connected:false` when no store is connected.
 *  Pass `provider` to read a NAMED source (the RESTORE STORY's explicit restore) — the server
 *  409s rather than silently reading a different cloud than the one the customer asked for. */
export async function cloudBackupLoad(provider?: "github" | "gdrive"): Promise<CloudBackupState> {
  let res: Response;
  try { res = await authedFetch(provider ? `backup?provider=${provider}` : "backup"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", `${provider === "gdrive" ? "Google Drive" : "GitHub"} isn't connected in your Oriora account.`);
  if (!res.ok) throw new BrainError("unavailable", "Couldn't read your cloud backup.");
  return (await res.json()) as CloudBackupState;
}

/** The RESTORE STORY status line — which clouds are connected, which one backups WRITE to, and the
 *  dated backup each holds. Ground truth from the relay (CUSTOMER-CLOUD-STORAGE.md §THE RESTORE STORY). */
export interface CloudBackupSlot { backup_exists: boolean; updated_at: string | null; bytes: number | null }
export interface CloudBackupStatusInfo {
  connected: ("github" | "gdrive")[];
  writes_to: "github" | "gdrive" | null;
  slots: Partial<Record<"github" | "gdrive", CloudBackupSlot>>;
}
export async function cloudBackupStatus(): Promise<CloudBackupStatusInfo> {
  let res: Response;
  try { res = await authedFetch("backup/status"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (!res.ok) throw new BrainError("unavailable", "Couldn't check your backup status.");
  return (await res.json()) as CloudBackupStatusInfo;
}

// Login opens the ecosystem web login INSIDE the app (its own WebKit window), not the OS
// browser — the in-app https handback is robust on iOS where the custom-scheme deep-link is
// fragile. Session returns via auth:deep-link (App.tsx + ipc.rs). The web callback must
// allowlist `oriora-stash` as a return target (server-side, operator task).
export const openWebLogin = () => invoke<void>("open_login_window");

// ── FILE A BOX FROM A PHOTO (§5b build 2026-08-19) — one photo of the open box → a DRAFT item
// list the customer reviews and bulk-adds. The photo transits for this call only; Oriora doesn't
// retain it. Suggestions only — nothing is saved until the customer confirms.
export interface FiledBoxItem { name: string; category: string; season: string }
export async function fileBoxFromPhoto(image: string): Promise<{ items: FiledBoxItem[]; confidence: string; note: string }> {
  const r = await brainCall<{ items?: FiledBoxItem[]; confidence?: string; note?: string }>("file-box", { image });
  return {
    items: Array.isArray(r.items) ? r.items.filter((i) => i && String(i.name ?? "").trim()).map((i) => ({
      name: String(i.name).trim().slice(0, 80),
      category: String(i.category ?? "").trim().slice(0, 30),
      season: String(i.season ?? "").trim().slice(0, 20),
    })) : [],
    confidence: String(r.confidence ?? "low"),
    note: String(r.note ?? "").trim(),
  };
}
